import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

interface BrandingSVGProps {
  /** Rendered width and height in px (or any CSS length). */
  size?: number | string;
  /** Play the summoning animation once on mount. */
  animated?: boolean;
  /** Seconds before the summoning animation starts. */
  delay?: number;
  /** Retained for existing call sites; the supplied badge uses the same detail at every size. */
  compact?: boolean;
  /** Golden glow around the transparent badge. */
  glow?: boolean;
  /** Keep the endless glow pulse running. Set false while off-screen. */
  ambient?: boolean;
  className?: string;
  title?: string;
}

const BADGE_SRC = "/brand/golden-crown-99.png";

/** The official golden 99 crown badge, shared by all brand mark placements. */
export function BrandingSVG({
  size = 96,
  animated = false,
  delay = 0,
  glow = false,
  ambient = true,
  className,
  title,
}: BrandingSVGProps) {
  const reduceMotion = useReducedMotion();
  const play = animated && !reduceMotion;
  const pulseGlow = glow && ambient && !reduceMotion;
  const at = (seconds: number) => delay + seconds;
  const glowFrames = [
    "drop-shadow(0 0 8px rgb(227 173 51 / 0.42))",
    "drop-shadow(0 0 22px rgb(227 173 51 / 0.78))",
    "drop-shadow(0 0 8px rgb(227 173 51 / 0.42))",
  ];

  return (
    <motion.img
      src={BADGE_SRC}
      alt={title ?? ""}
      aria-hidden={title ? undefined : true}
      draggable={false}
      width={typeof size === "number" ? size : undefined}
      height={typeof size === "number" ? size : undefined}
      className={cn("block max-w-none select-none object-contain", className)}
      style={{ width: size, height: size }}
      initial={play ? { scale: 0.4, opacity: 0, rotate: -30 } : false}
      animate={{
        scale: 1,
        opacity: 1,
        rotate: 0,
        filter: glow ? (pulseGlow ? glowFrames : glowFrames[1]) : "none",
      }}
      transition={{
        default: { delay: at(0), type: "spring", stiffness: 80, damping: 13 },
        filter: {
          delay: at(1.2),
          duration: 3.6,
          repeat: pulseGlow ? Infinity : 0,
          ease: "easeInOut",
        },
      }}
    />
  );
}
